import type { IUseModal } from "@fluidattacks/design";
import * as React from "react";
import { useCallback } from "react";

import { CreateMailmapEntryModal } from "features/mailmap/create-modal";
import { DeleteMailmapEntryModal } from "features/mailmap/delete-modal";
import { ImportMailmapModal } from "features/mailmap/import-mailmap-modal";
import { MoveMailmapEntryWithSubentriesModal } from "features/mailmap/move-entry-with-subentries-modal";
import { SetMailmapEntryAsMailmapSubentryModal } from "features/mailmap/set-entry-as-subentry-modal";
import type {
  ICreateMailmapEntryFormValues,
  IDeleteMailmapEntryFormValues,
  IImportMailmapFormValues,
  IMailmapEntry,
  IMoveMailmapEntryWithSubentriesFormValues,
  ISetMailmapEntryAsMailmapSubentryFormValues,
  IUpdateMailmapEntryFormValues,
} from "features/mailmap/types";
import { UpdateMailmapEntryModal } from "features/mailmap/update-modal";

interface IMailmapModalsProps {
  checkedAuthors: IMailmapEntry[];
  createEntryModal: IUseModal;
  deleteEntryModal: IUseModal;
  updateEntryModal: IUseModal;
  setEntryAsSubentryModal: IUseModal;
  moveEntryWithSubentriesModal: IUseModal;
  importMailmapModal: IUseModal;
  createMailmapEntry: (values: ICreateMailmapEntryFormValues) => Promise<void>;
  deleteMailmapEntry: (values: IDeleteMailmapEntryFormValues) => Promise<void>;
  updateMailmapEntry: (values: IUpdateMailmapEntryFormValues) => Promise<void>;
  setMailmapEntryAsMailmapSubentry: (
    values: ISetMailmapEntryAsMailmapSubentryFormValues,
  ) => Promise<void>;
  moveMailmapEntryWithSubentries: (
    values: IMoveMailmapEntryWithSubentriesFormValues,
  ) => Promise<void>;
  importMailmap: (values: IImportMailmapFormValues) => Promise<void>;
  importMailmapSubmitting: boolean;
}

export const MailmapModals = ({
  checkedAuthors,
  createEntryModal,
  deleteEntryModal,
  updateEntryModal,
  setEntryAsSubentryModal,
  moveEntryWithSubentriesModal,
  importMailmapModal,
  createMailmapEntry,
  deleteMailmapEntry,
  updateMailmapEntry,
  setMailmapEntryAsMailmapSubentry,
  moveMailmapEntryWithSubentries,
  importMailmap,
  importMailmapSubmitting,
}: IMailmapModalsProps): JSX.Element => {
  const getSelectedEntryEmail = (): string =>
    checkedAuthors.length === 1 ? checkedAuthors[0].mailmapEntryEmail : "";

  const getSelectedEntryName = (): string =>
    checkedAuthors.length === 1 ? checkedAuthors[0].mailmapEntryName : "";

  const handleCloseCreateEntryModal = useCallback((): void => {
    createEntryModal.close();
  }, [createEntryModal]);
  const handleCloseDeleteEntryModal = useCallback((): void => {
    deleteEntryModal.close();
  }, [deleteEntryModal]);
  const handleCloseUpdateEntryModal = useCallback((): void => {
    updateEntryModal.close();
  }, [updateEntryModal]);
  const handleCloseSetEntryAsSubentryModal = useCallback((): void => {
    setEntryAsSubentryModal.close();
  }, [setEntryAsSubentryModal]);
  const handleCloseMoveEntryWithSubentriesModal = useCallback((): void => {
    moveEntryWithSubentriesModal.close();
  }, [moveEntryWithSubentriesModal]);
  const handleCloseImportMailmapModal = useCallback((): void => {
    importMailmapModal.close();
  }, [importMailmapModal]);

  return (
    <React.Fragment>
      {createEntryModal.isOpen ? (
        <CreateMailmapEntryModal
          modalRef={createEntryModal}
          onClose={handleCloseCreateEntryModal}
          onSubmit={createMailmapEntry}
        />
      ) : undefined}
      {deleteEntryModal.isOpen ? (
        <DeleteMailmapEntryModal
          entryEmail={getSelectedEntryEmail()}
          modalRef={deleteEntryModal}
          onClose={handleCloseDeleteEntryModal}
          onSubmit={deleteMailmapEntry}
        />
      ) : undefined}
      {updateEntryModal.isOpen ? (
        <UpdateMailmapEntryModal
          entryEmail={getSelectedEntryEmail()}
          entryName={getSelectedEntryName()}
          modalRef={updateEntryModal}
          onClose={handleCloseUpdateEntryModal}
          onSubmit={updateMailmapEntry}
        />
      ) : undefined}
      {setEntryAsSubentryModal.isOpen ? (
        <SetMailmapEntryAsMailmapSubentryModal
          entryEmail={getSelectedEntryEmail()}
          modalRef={setEntryAsSubentryModal}
          onClose={handleCloseSetEntryAsSubentryModal}
          onSubmit={setMailmapEntryAsMailmapSubentry}
        />
      ) : undefined}
      {moveEntryWithSubentriesModal.isOpen ? (
        <MoveMailmapEntryWithSubentriesModal
          entryEmail={getSelectedEntryEmail()}
          modalRef={moveEntryWithSubentriesModal}
          onClose={handleCloseMoveEntryWithSubentriesModal}
          onSubmit={moveMailmapEntryWithSubentries}
        />
      ) : undefined}
      {importMailmapModal.isOpen ? (
        <ImportMailmapModal
          modalRef={importMailmapModal}
          onClose={handleCloseImportMailmapModal}
          onSubmit={importMailmap}
          submitting={importMailmapSubmitting}
        />
      ) : undefined}
    </React.Fragment>
  );
};
